import axios, { AxiosInstance, AxiosResponse } from "axios";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import config from "../config/env";
import { tokenRefreshService } from "./tokenRefreshService";

export interface SettingsData {
  id: number;
  userId: number;
  appCd: string | null;
  collaboUserid: string | null;
  collaboApiKey: string | null;
  bugyoAccessToken: string | null;
  bugyoSupscriptionKey: string | null;
  bugyoTenantId: string | null;
  createdAt: string;
  createdBy: number | null;
  updatedAt: string;
  updatedBy: number | null;
  deletedAt: string | null;
  deletedBy: number | null;
  apiKeyId: number;
}

export interface ApiKeyData {
  id: number;
  encryptedKey: string;
  settingId: number;
  isDisabled: boolean;
  createdAt: string;
  createdBy: number;
  updatedAt: string;
  updatedBy: number | null;
  deletedAt: string | null;
  deletedBy: number | null;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

class SettingsService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: config.apiUrl,
      timeout: 15000,
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      config => {
        const token = Cookies.get("access_token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => {
        console.error("Settings request error:", error);
        return Promise.reject(error);
      }
    );

    // Setup automatic token refresh FIRST (before other interceptors)
    tokenRefreshService.setupAxiosInterceptor(this.client);

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      response => {
        // Check for API errors in successful HTTP responses (200 status with error in body)
        if (response.data && response.data.success === false) {
          // DEPRECATED: Temporarily disabled error navigation
          // navigationService.handleApiFailure();

          // Transform the response to error format
          const apiError = response.data;
          return Promise.reject({
            success: false,
            message:
              apiError.error ||
              apiError.message ||
              "サーバーエラーが発生しました",
            errors: apiError.errors || [
              {
                field: "general",
                message: apiError.message || "サーバーエラーが発生しました",
              },
            ],
          });
        }

        return response;
      },
      error => {
        console.error(
          "Settings response error:",
          error.response?.data || error.message
        );

        // Handle timeout errors
        if (
          error.code === "ECONNABORTED" ||
          error.message.includes("timeout")
        ) {
          toast.error(
            "リクエストがタイムアウトしました。しばらく待ってから再試行してください。"
          );
          return Promise.reject({
            success: false,
            message:
              "リクエストがタイムアウトしました。しばらく待ってから再試行してください。",
            errors: [
              {
                field: "general",
                message:
                  "ネットワーク接続が遅いか、サーバーが応答していません。",
              },
            ],
            isTimeout: true,
          });
        }

        // Don't handle 401 errors - let token refresh interceptor handle them
        if (error.response?.status === 401) {
          return Promise.reject(error);
        }

        // Handle API failures with navigation callback (except 401 which is handled by token refresh)
        if (error.response?.status >= 400) {
          // DEPRECATED: Temporarily disabled error navigation
          // navigationService.handleApiFailure();
        }

        if (error.response?.data) {
          const apiError = error.response.data;
          return Promise.reject({
            success: false,
            message:
              apiError.error ||
              apiError.message ||
              "サーバーエラーが発生しました",
            errors: apiError.errors || [
              {
                field: "general",
                message: apiError.message || "サーバーエラーが発生しました",
              },
            ],
            status: error.response?.status, // Preserve status code
            response: error.response, // Preserve response for status checking
          });
        }

        return Promise.reject({
          success: false,
          message: "ネットワークエラーが発生しました",
          errors: [
            {
              field: "general",
              message:
                "サーバーに接続できません。ネットワークを確認してください。",
            },
          ],
        });
      }
    );
  }

  private async makeRequest<T>(
    endpoint: string,
    options: any = {}
  ): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.client.request(
        {
          url: endpoint,
          method: options.method || "GET",
          data: options.body ? JSON.parse(options.body) : undefined,
          ...options,
        }
      );

      return response.data;
    } catch (error) {
      console.error("API request failed:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "An error occurred",
      };
    }
  }

  async getSettings(): Promise<ApiResponse<SettingsData>> {
    return this.makeRequest<SettingsData>("/settings");
  }

  async saveSettings(
    settings: Partial<SettingsData>
  ): Promise<ApiResponse<void>> {
    return this.makeRequest<void>("/settings", {
      method: "POST",
      body: JSON.stringify(settings),
    });
  }

  async getApiKey(apiKeyId: number): Promise<ApiResponse<ApiKeyData>> {
    return this.makeRequest<ApiKeyData>(`/api_key/${apiKeyId}`);
  }

  async generateNewApiKey(settingId: number): Promise<ApiResponse<ApiKeyData>> {
    return this.makeRequest<ApiKeyData>("/api_key", {
      method: "POST",
      body: JSON.stringify({ settingId: settingId }),
    });
  }

  async toggleApiKeyStatus(
    apiKeyId: number,
    isDisabled: boolean
  ): Promise<ApiResponse<ApiKeyData>> {
    return this.makeRequest<ApiKeyData>(`/api_key/${apiKeyId}`, {
      method: "POST",
      body: JSON.stringify({ isDisabled: isDisabled }),
    });
  }

  async getAppCd(): Promise<ApiResponse<string | null>> {
    return this.makeRequest<string | null>("/settings/app_cd");
  }

  async deleteAppCd(): Promise<ApiResponse<void>> {
    return this.makeRequest<void>("/settings/app_cd", {
      method: "DELETE",
    });
  }
}

export const settingsService = new SettingsService();
